import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  getChildren,
  searchActivities as searchActivitiesApi,
  solverSolve,
  solverAcceptProposal,
} from '../../util/api';
import { storableError } from '../../util/errors';

// ─── Thunks ──────────────────────────────────────────────────────────────────

export const fetchCampScoutChildren = createAsyncThunk(
  'campScout/fetchChildren',
  async (_: void, { rejectWithValue }) => {
    try {
      const result = await getChildren();
      const children = result?.data || result || [];
      // Normalize to solver-friendly shape: { id, name, birthYear }
      return (children as any[]).map((c: any) => ({
        id: c.id,
        name: c.childName || c.name || null,
        birthYear: c.birthYear || null,
        profileImageUrl: c.profileImageUrl || null,
      }));
    } catch (error) {
      return rejectWithValue(storableError(error));
    }
  }
);

export const searchActivities = createAsyncThunk(
  'campScout/searchActivities',
  async (params: Record<string, string> | undefined, { rejectWithValue }) => {
    try {
      const result = await searchActivitiesApi(params || {});
      // searchActivities returns { activities: [...], neighborhoods: [...], ... }
      return result?.activities || result?.data?.activities || result?.data || result || [];
    } catch (error) {
      return rejectWithValue(storableError(error));
    }
  }
);

export const requestSolve = createAsyncThunk(
  'campScout/solve',
  async (
    params: {
      planningWindow: { startsAt: string; endsAt: string };
      children: any[];
      basketItems: any[];
      blackouts?: any[];
      objectiveWeights?: any;
      options?: any;
    },
    { rejectWithValue }
  ) => {
    try {
      // eslint-disable-next-line no-console
      console.groupCollapsed('%c[CampScout] solve REQUEST', 'color:#f97316;font-weight:bold');
      // eslint-disable-next-line no-console
      console.log(params);
      // eslint-disable-next-line no-console
      console.groupEnd();
      const result = await solverSolve(params);
      const payload = result?.data || result || {};
      // eslint-disable-next-line no-console
      console.groupCollapsed(
        '%c[CampScout] solve RESPONSE',
        'color:#16a34a;font-weight:bold',
        `proposals=${payload?.proposals?.length || 0}`,
        `solveTimeMs=${payload?.solveTimeMs || 0}`
      );
      // eslint-disable-next-line no-console
      console.log('full payload:', payload);
      if (Array.isArray(payload?.debug)) {
        payload.debug.forEach((entry: any) => {
          // eslint-disable-next-line no-console
          console.log(`%c[${entry.step}] ${entry.message}`, 'color:#0ea5e9', entry.data);
        });
      }
      // eslint-disable-next-line no-console
      console.groupEnd();
      return payload;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[CampScout] solve ERROR', error);
      return rejectWithValue(storableError(error));
    }
  }
);

export const acceptProposal = createAsyncThunk(
  'campScout/acceptProposal',
  async (params: { proposalItems: any[] }, { rejectWithValue }) => {
    try {
      const result = await solverAcceptProposal(params);
      return result?.data || result || {};
    } catch (error) {
      return rejectWithValue(storableError(error));
    }
  }
);

// ─── Slice ───────────────────────────────────────────────────────────────────

interface CampScoutState {
  children: any[];
  childrenLoading: boolean;
  childrenError: any;

  activities: any[];
  activitiesLoading: boolean;
  activitiesError: any;

  proposals: any[];
  selectedProposalIndex: number | null;
  solveInProgress: boolean;
  solveError: any;

  acceptInProgress: boolean;
  acceptError: any;
  acceptedCart: any;
}

const initialState: CampScoutState = {
  children: [],
  childrenLoading: false,
  childrenError: null,

  activities: [],
  activitiesLoading: false,
  activitiesError: null,

  proposals: [],
  selectedProposalIndex: null,
  solveInProgress: false,
  solveError: null,

  acceptInProgress: false,
  acceptError: null,
  acceptedCart: null,
};

const campScoutSlice = createSlice({
  name: 'campScout',
  initialState,
  reducers: {
    selectProposal: (state, action) => {
      state.selectedProposalIndex = action.payload;
    },
    clearProposals: state => {
      state.proposals = [];
      state.selectedProposalIndex = null;
      state.solveError = null;
    },
    clearAcceptedCart: state => {
      state.acceptedCart = null;
      state.acceptError = null;
    },
    hydrateProposals: (state, action) => {
      // Restore proposals from localStorage on page mount so refresh doesn't
      // wipe a freshly-solved plan.
      state.proposals = action.payload?.proposals || [];
      state.selectedProposalIndex = action.payload?.selectedProposalIndex ?? null;
    },
  },
  extraReducers: builder => {
    builder
      // Children
      .addCase(fetchCampScoutChildren.pending, state => {
        state.childrenLoading = true;
        state.childrenError = null;
      })
      .addCase(fetchCampScoutChildren.fulfilled, (state, action) => {
        state.childrenLoading = false;
        state.children = action.payload;
      })
      .addCase(fetchCampScoutChildren.rejected, (state, action) => {
        state.childrenLoading = false;
        state.childrenError = action.payload || action.error;
      })

      // Activities
      .addCase(searchActivities.pending, state => {
        state.activitiesLoading = true;
        state.activitiesError = null;
      })
      .addCase(searchActivities.fulfilled, (state, action) => {
        state.activitiesLoading = false;
        state.activities = action.payload;
      })
      .addCase(searchActivities.rejected, (state, action) => {
        state.activitiesLoading = false;
        state.activitiesError = action.payload || action.error;
      })

      // Solve
      .addCase(requestSolve.pending, state => {
        // Keep existing proposals visible while a new solve is running so the
        // user doesn't lose context. They get replaced on fulfilled.
        state.solveInProgress = true;
        state.solveError = null;
      })
      .addCase(requestSolve.fulfilled, (state, action) => {
        state.solveInProgress = false;
        const newProposals = action.payload?.proposals || [];
        // Only swap in the new ones if the solve actually returned proposals;
        // otherwise keep the previous ones so the user doesn't lose them.
        if (newProposals.length > 0) {
          state.proposals = newProposals;
          state.selectedProposalIndex = 0;
        }
      })
      .addCase(requestSolve.rejected, (state, action) => {
        state.solveInProgress = false;
        state.solveError = action.payload || action.error;
      })

      // Accept proposal
      .addCase(acceptProposal.pending, state => {
        state.acceptInProgress = true;
        state.acceptError = null;
      })
      .addCase(acceptProposal.fulfilled, (state, action) => {
        state.acceptInProgress = false;
        state.acceptedCart = action.payload;
      })
      .addCase(acceptProposal.rejected, (state, action) => {
        state.acceptInProgress = false;
        state.acceptError = action.payload || action.error;
      });
  },
});

export const { selectProposal, clearProposals, clearAcceptedCart, hydrateProposals } =
  campScoutSlice.actions;

// ─── Selectors ───────────────────────────────────────────────────────────────

export const selectCampScoutChildren = (state: any) => state.CampScoutPage.children;
export const selectCampScoutChildrenLoading = (state: any) => state.CampScoutPage.childrenLoading;
export const selectCampScoutActivities = (state: any) => state.CampScoutPage.activities;
export const selectCampScoutActivitiesLoading = (state: any) => state.CampScoutPage.activitiesLoading;
export const selectProposals = (state: any) => state.CampScoutPage.proposals;
export const selectSelectedProposalIndex = (state: any) => state.CampScoutPage.selectedProposalIndex;
export const selectSolveInProgress = (state: any) => state.CampScoutPage.solveInProgress;
export const selectSolveError = (state: any) => state.CampScoutPage.solveError;
export const selectAcceptInProgress = (state: any) => state.CampScoutPage.acceptInProgress;
export const selectAcceptedCart = (state: any) => state.CampScoutPage.acceptedCart;

// ─── loadData ────────────────────────────────────────────────────────────────

export const loadData = () => (dispatch: any) => {
  return Promise.all([
    dispatch(fetchCampScoutChildren()),
    dispatch(searchActivities({})),
  ]);
};

export default campScoutSlice.reducer;
